import { useSession } from '@zhiyue/core';
import React from 'react';
import { fetchResearchState } from './apiBridge';
import { createEmptyState } from './initialData';
import { demoReducer } from './reducer';
import type { DemoAction, DemoState } from './types';
import { hasOAuth2Session } from '@/services/oauth2';

const DemoStateContext = React.createContext<DemoState | null>(null);
const DemoDispatchContext =
  React.createContext<React.Dispatch<DemoAction> | null>(null);

export type ResearchSync = {
  loading: boolean;
  error: string | null;
  ready: boolean;
  refresh: () => Promise<void>;
};
const ResearchSyncContext = React.createContext<ResearchSync | null>(null);

/**
 * 研究域会话状态。
 * - 记录集合初始为空，挂载后从 /bff/research/* 分接口拉取真实数据；
 * - 拉取失败展示错误并提供重试，不再回退到演示数据。
 */
export const DemoDataProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { currentUser, loading: sessionLoading } = useSession();
  const [state, dispatch] = React.useReducer(
    demoReducer,
    undefined,
    createEmptyState,
  );
  const [sync, setSync] = React.useState<ResearchSync>({
    loading: true,
    error: null,
    ready: false,
    refresh: async () => {},
  });

  const hydrate = React.useCallback(async () => {
    setSync((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await fetchResearchState();
      dispatch({ type: 'research/hydrated', payload: data });
      setSync({ loading: false, error: null, ready: true, refresh: () => hydrate() });
    } catch (error) {
      setSync({
        loading: false,
        error: error instanceof Error ? error.message : '研究数据加载失败',
        ready: false,
        refresh: () => hydrate(),
      });
    }
  }, []);

  React.useEffect(() => {
    if (sessionLoading || !hasOAuth2Session() || !currentUser) return;
    void hydrate();
  }, [hydrate, sessionLoading, currentUser]);

  return (
    <DemoStateContext.Provider value={state}>
      <DemoDispatchContext.Provider value={dispatch}>
        <ResearchSyncContext.Provider
          value={
            sessionLoading || !hasOAuth2Session() || !currentUser
              ? { loading: false, error: null, ready: true, refresh: async () => {} }
              : sync
          }
        >
          {children}
        </ResearchSyncContext.Provider>
      </DemoDispatchContext.Provider>
    </DemoStateContext.Provider>
  );
};

export const useDemoState = () => {
  const value = React.useContext(DemoStateContext);
  if (!value)
    throw new Error('useDemoState must be used inside DemoDataProvider');
  return value;
};

export const useDemoDispatch = () => {
  const value = React.useContext(DemoDispatchContext);
  if (!value)
    throw new Error('useDemoDispatch must be used inside DemoDataProvider');
  return value;
};

export const useDemoSelector = <T,>(selector: (state: DemoState) => T): T =>
  selector(useDemoState());

export const useResearchSync = (): ResearchSync => {
  const value = React.useContext(ResearchSyncContext);
  if (!value)
    throw new Error('useResearchSync must be used inside DemoDataProvider');
  return value;
};
